// app/providers.tsx
'use client';

import { useUser } from '@clerk/nextjs';
import { usePathname, useSearchParams } from 'next/navigation';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react';
import React, { Suspense, useEffect } from 'react';
import { useCookieConsent } from './context/cookieConsentContext';

// Extend PostHog type to include our custom property
declare module 'posthog-js' {
  interface PostHog {
    __initialized?: boolean;
  }
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const { consentGiven, isMounted } = useCookieConsent();

  useEffect(() => {
    if (!isMounted || typeof window === 'undefined') return;

    // Allow disabling PostHog via public env flag
    const posthogDisabled = process.env.NEXT_PUBLIC_DISABLE_POSTHOG === 'true';
    if (posthogDisabled) {
      return;
    }

    const persistence = consentGiven === 'accepted' ? 'localStorage+cookie' : 'memory';

    // Init once
    if (!posthog.__initialized) {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_API_HOST || '/budget-insights',
        ui_host: process.env.NEXT_PUBLIC_POSTHOG_UI_HOST || 'https://eu.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: false,
        persistence,
        loaded: () => {
          posthog.__initialized = true;
        },
      });
    }

    // Update persistence and capturing state based on consent
    posthog.set_config({ persistence });
    if (consentGiven === 'accepted') {
      posthog.opt_in_capturing();
    } else {
      posthog.opt_out_capturing();
    }
  }, [consentGiven, isMounted]);

  return (
    <PHProvider client={posthog}>
      <SuspendedPostHogPageView />
      <PostHogClerkFlagPersonProperties />
      {children}
    </PHProvider>
  );
}

/** Syncs Clerk email into PostHog flag evaluation once; call sites can keep using useFeatureFlagEnabled as usual. */
function PostHogClerkFlagPersonProperties() {
  const posthogClient = usePostHog();
  const { isLoaded, isSignedIn, user } = useUser();
  const { consentGiven, isMounted } = useCookieConsent();

  useEffect(() => {
    const posthogDisabled = process.env.NEXT_PUBLIC_DISABLE_POSTHOG === 'true';
    if (posthogDisabled || !isMounted || !posthogClient) return;

    if (consentGiven !== 'accepted') {
      posthogClient.resetPersonPropertiesForFlags();
      return;
    }

    if (!isLoaded) return;

    const email = user?.primaryEmailAddress?.emailAddress;
    if (!isSignedIn || !email) {
      posthogClient.resetPersonPropertiesForFlags();
      return;
    }

    posthogClient.setPersonPropertiesForFlags({ email }, true);
  }, [
    posthogClient,
    consentGiven,
    isMounted,
    isLoaded,
    isSignedIn,
    user?.primaryEmailAddress?.emailAddress,
  ]);

  return null;
}

function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();
  const { consentGiven } = useCookieConsent();

  // Track pageviews
  useEffect(() => {
    // Allow disabling PostHog via public env flag
    const posthogDisabled = process.env.NEXT_PUBLIC_DISABLE_POSTHOG === 'true';
    if (posthogDisabled) return;

    // Only send pageviews if consent is given
    if (consentGiven === 'accepted' && pathname && posthog) {
      let url = window.origin + pathname;
      if (searchParams.toString()) {
        url = url + '?' + searchParams.toString();
      }

      posthog.capture('$pageview', { $current_url: url });
    }
  }, [pathname, searchParams, posthog, consentGiven]);

  return null;
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}
